import React, { useState, useRef, useEffect } from 'react';

interface EditableFieldProps {
  value: string;
  onSave: (value: string) => void;
  onCancel?: () => void;
  placeholder?: string;
  className?: string;
  multiline?: boolean;
  autoFocus?: boolean;
  readOnly?: boolean;
  readOnlyTitle?: string;
}

export const EditableField: React.FC<EditableFieldProps> = ({
  value,
  onSave,
  onCancel,
  placeholder = '',
  className = '',
  multiline = false,
  autoFocus = false,
  readOnly = false,
  readOnlyTitle,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    if (readOnly) return;
    setIsEditing(true);
    setEditValue(value);
  };

  const handleSave = () => {
    onSave(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
    onCancel?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    const InputComponent = multiline ? 'textarea' : 'input';
    return (
      <InputComponent
        ref={inputRef as any}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`bg-transparent border-none outline-none text-inherit font-inherit inline-block min-w-0 ${className}`}
        style={{ minWidth: 'fit-content' }}
        autoFocus={autoFocus}
        rows={multiline ? 3 : undefined}
      />
    );
  }

  if (readOnly) {
    return (
      <span
        className={`px-1 py-0.5 rounded inline-block min-w-0 cursor-not-allowed opacity-80 ${className}`}
        title={readOnlyTitle ?? 'Valeur calculée — modifiez les paramètres pour la recalculer'}
        style={{ minWidth: 'fit-content' }}
      >
        {value || placeholder}
      </span>
    );
  }

  return (
    <span
      onClick={handleStartEdit}
      className={`cursor-pointer hover:bg-gray-100 px-1 py-0.5 rounded inline-block min-w-0 ${className}`}
      title="Cliquer pour éditer"
      style={{ minWidth: 'fit-content' }}
    >
      {value || placeholder}
    </span>
  );
};
